import { Inject, Injectable, Optional } from '@angular/core';
import {
  MEDDICScores,
  MEDDICSection,
  MEDDICSectionData,
  OpportunityScoring,
  OpportunityScoringService,
} from '@vendasta/sales-opportunities';
import { Observable, SchedulerLike } from 'rxjs';
import { ObservableWorkStateMap, WorkStateMap } from '@vendasta/rx-utils/work-state';

import { map, shareReplay } from 'rxjs/operators';
import { DataProviderConfig, IDENTITY, KeyedDataProvider } from '@vendasta/data-providers';

export function isFailed$<ID>(stateMap: WorkStateMap<ID, any>, id: ID): Observable<boolean> {
  return stateMap.isSuccess$(id).pipe(map((success) => !success));
}

// Currently equivalent types, but might diverge later
export interface MEDDICScoreCommands {
  submitMEDDICScores(accountGroupId: string, opportunityId: string, meddicScores: MEDDICScores): void;
}

export interface MEDDICScoreState {
  isSubmitting$(opportunityId: string): Observable<boolean>;

  getCachedScoresOrFetch$(accountGroupId: string, opportunityId: string): Observable<MEDDICScores>;
}

interface ScoreInputs {
  accountGroupId: string;
  opportunityId: string;
}

/**
 * An app-level source of MEDDIC scores for opportunities and commands that can
 * be used to modify them both app-wide and on the backing server.
 */
@Injectable({ providedIn: 'root' })
export class MEDDICScoreSource implements MEDDICScoreCommands, MEDDICScoreState {
  private readonly scores: KeyedDataProvider<ScoreInputs, MEDDICScores, MEDDICScores>;

  private readonly updateScoresStateMap = new ObservableWorkStateMap<string, null>();
  private readonly updateNotesStateMap = new ObservableWorkStateMap<string, string>();
  private readonly getSectionsStateMap = new ObservableWorkStateMap<string, MEDDICSectionData[]>();

  constructor(
    @Inject(OpportunityScoringService) private readonly scoreSvc: OpportunityScoring,
    @Inject('scheduler') @Optional() scheduler: SchedulerLike,
  ) {
    const c: DataProviderConfig<ScoreInputs, MEDDICScores, MEDDICScores> = {
      externalSource: {
        fetchFn: (i: ScoreInputs) => {
          if (!!i?.accountGroupId && !!i?.opportunityId) {
            return this.scoreSvc.getMEDDICScores(i.accountGroupId, i.opportunityId);
          }
          return null;
        },
        uploadFn: (i: ScoreInputs, d: MEDDICScores) =>
          this.scoreSvc.updateMEDDICScores(i.accountGroupId, i.opportunityId, d),
      },
      convert: {
        inputsToStringKey: (i: ScoreInputs) => i.opportunityId,
        apiDataToAppData: IDENTITY,
      },
      testing: { scheduler: scheduler || undefined },
    };
    this.scores = new KeyedDataProvider(c);
  }

  initializeMeddicSections(accountGroupId: string, opportunityId: string): void {
    const getSectionsFn = this.scoreSvc.getMEDDICSections(accountGroupId, opportunityId);
    this.getSectionsStateMap.startWork(opportunityId, getSectionsFn);
  }

  getMeddicSections(opportunityId: string): Observable<MEDDICSectionData[]> {
    return this.getSectionsStateMap.getWorkResults$(opportunityId);
  }

  getCachedScoresOrFetch$(accountGroupId: string, opportunityId: string): Observable<MEDDICScores> {
    return this.scores.getCachedOrFetch$({ accountGroupId: accountGroupId, opportunityId: opportunityId });
  }

  submitMEDDICScores(accountGroupId: string, opportunityId: string, meddicScores: MEDDICScores): void {
    this.scores.store({ accountGroupId: accountGroupId, opportunityId: opportunityId }, meddicScores);
  }

  submitMEDDICNotes(
    accountGroupId: string,
    opportunityId: string,
    salesPersonId: string,
    meddicSection: MEDDICSection,
    notes: string,
  ): void {
    const work = this.scoreSvc
      .updateMEDDICNotes(accountGroupId, opportunityId, salesPersonId, meddicSection, notes)
      .pipe(shareReplay(1));
    this.updateNotesStateMap.startWork(opportunityId, work);
  }

  isSubmitting$(opportunityId: string): Observable<boolean> {
    return this.updateScoresStateMap.isLoading$(opportunityId);
  }

  isSubmitFailed$(id: string): Observable<boolean> {
    return isFailed$(this.updateScoresStateMap, id);
  }

  submissionSuccessEvents$(opportunityId: string): Observable<boolean> {
    return this.updateScoresStateMap.successEvent$(opportunityId);
  }

  isLoading$(id: string): Observable<boolean> {
    return this.scores.state.isLoading$(id);
  }

  isLoadSuccess(id: string): Observable<boolean> {
    return this.scores.state.isSuccess$(id);
  }

  notesSuccessEvent$(id: string): Observable<boolean> {
    return this.updateNotesStateMap.successEvent$(id);
  }

  didNotesSaveSuccessfully$(id: string): Observable<boolean> {
    return this.updateNotesStateMap.isSuccess$(id);
  }

  areNotesCurrentlySaving$(id: string): Observable<boolean> {
    return this.updateNotesStateMap.isLoading$(id);
  }

  areSectionsCurrentlyLoading$(id: string): Observable<boolean> {
    return this.getSectionsStateMap.isLoading$(id);
  }

  didSectionsLoadSuccessfully(id: string): Observable<boolean> {
    return this.getSectionsStateMap.isSuccess$(id);
  }

  notesSaveEvents$(id: string): Observable<boolean> {
    return this.updateNotesStateMap.successEvent$(id);
  }
}
